// Single source of truth for Omni roles, admin tiers, permissions, scopes,
// and audit action labels.
// Anyone may revoke their own AI connection as a server rule; grants.revoke is for revoking other people's.
// Business invariant: admin tiers require a phoneware.us email address.

export const DEALER_ROLES = ["owner", "sales", "tech"] as const;
export type DealerRole = (typeof DEALER_ROLES)[number];

export const ADMIN_TIERS = [
  "admin_super",
  "admin_billing",
  "admin_sales",
  "admin_support",
] as const;
export type AdminTier = (typeof ADMIN_TIERS)[number];

export const ROLES = [...DEALER_ROLES, ...ADMIN_TIERS] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  owner: "Owner",
  sales: "Sales",
  tech: "Tech",
  admin_super: "Super admin",
  admin_billing: "Billing ops",
  admin_sales: "Sales",
  admin_support: "Support",
};

export const ADMIN_TIER_LABELS: Record<AdminTier, string> = {
  admin_super: "Super admin",
  admin_billing: "Billing ops",
  admin_sales: "Sales",
  admin_support: "Support",
};

export const DEALER_ROLE_LABELS: Record<DealerRole, string> = {
  owner: "Owner",
  sales: "Sales",
  tech: "Tech",
};

export function roleLabel(role: Role): string {
  return ROLE_LABELS[role];
}

export function isAdminRole(role: string): role is AdminTier {
  return (ADMIN_TIERS as readonly string[]).includes(role);
}

export function isDealerRole(role: string): role is DealerRole {
  return (DEALER_ROLES as readonly string[]).includes(role);
}

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}

export const PERMISSIONS = [
  "all_dealers.read",
  "catalog.read_all",
  "quotes.write",
  "quote_intakes.kqm",
  "customers.write",
  "customers.contacts",
  "mac.start",
  "approvals.decide",
  "billing.approve",
  "orders.run",
  "orders.edit",
  "billing.accounts",
  "catalog.link",
  "catalog.manage",
  "sync.run",
  "customers.assign",
  "dealers.manage",
  "users.manage",
  "admins.manage",
  "grants.revoke",
  "act_as",
  "customer_accounts.review",
  "sso.all_customers",
  "sso.link_unmatched_ns",
  "service_links.write",
  "service_links.billing",
  "requests.create",
  "requests.comment",
  "requests.run",
  "requests.manage",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export function isPermission(value: unknown): value is Permission {
  return typeof value === "string" && (PERMISSIONS as readonly string[]).includes(value);
}

const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  admin_super: [
    "all_dealers.read",
    "catalog.read_all",
    "quotes.write",
    "quote_intakes.kqm",
    "customers.write",
    "customers.contacts",
    "mac.start",
    "approvals.decide",
    "billing.approve",
    "orders.run",
    "orders.edit",
    "billing.accounts",
    "catalog.link",
    "catalog.manage",
    "sync.run",
    "customers.assign",
    "dealers.manage",
    "users.manage",
    "admins.manage",
    "grants.revoke",
    "act_as",
    "customer_accounts.review",
    "sso.all_customers",
    "sso.link_unmatched_ns",
    "service_links.write",
    "service_links.billing",
    "requests.create",
    "requests.comment",
    "requests.run",
    "requests.manage",
  ],
  admin_billing: [
    "all_dealers.read",
    "catalog.read_all",
    "approvals.decide",
    "billing.approve",
    "orders.run",
    "orders.edit",
    "billing.accounts",
    "catalog.link",
    "sync.run",
    "customer_accounts.review",
    "sso.all_customers",
    "service_links.billing",
    "requests.create",
    "requests.comment",
  ],
  admin_sales: [
    "all_dealers.read",
    "catalog.read_all",
    "quotes.write",
    "quote_intakes.kqm",
    "customers.write",
    "customers.contacts",
    "mac.start",
    "sso.all_customers",
    "requests.create",
    "requests.comment",
  ],
  admin_support: [
    "all_dealers.read",
    "catalog.read_all",
    "mac.start",
    "sso.all_customers",
    "requests.create",
    "requests.comment",
    "requests.run",
    "requests.manage",
  ],
  owner: [
    "quotes.write",
    "customers.write",
    "customers.contacts",
    "orders.edit",
    "billing.accounts",
    "dealers.manage",
    "users.manage",
    "grants.revoke",
    "requests.create",
    "requests.comment",
  ],
  sales: [
    "quotes.write",
    "customers.write",
    "customers.contacts",
    "orders.edit",
    "billing.accounts",
    "grants.revoke",
    "requests.create",
    "requests.comment",
  ],
  tech: [
    "customers.contacts",
    "orders.edit",
    "grants.revoke",
    "requests.create",
    "requests.comment",
  ],
};

export function permissionsOf(role: Role): readonly Permission[] {
  return ROLE_PERMISSIONS[role] ?? [];
}
export function hasPermission(role: Role, permission: Permission): boolean {
  return permissionsOf(role).includes(permission);
}

export type WriteScope = "own" | "house" | "any";

const WRITE_SCOPES: Partial<Record<Permission, Partial<Record<Role, WriteScope>>>> = {
  "quotes.write": {
    admin_super: "any",
    // D1, Jason 2026-10-23: Sales quotes for any dealer, not only the house dealer.
    admin_sales: "any",
    owner: "own",
    sales: "own",
  },
  "customers.write": {
    admin_super: "any",
    admin_sales: "any",
    owner: "own",
    sales: "own",
  },
  "customers.contacts": {
    admin_super: "any",
    admin_sales: "any",
    owner: "own",
    sales: "own",
    tech: "own",
  },
  "mac.start": {
    admin_super: "any",
    admin_sales: "any",
    admin_support: "any",
  },
  "orders.edit": {
    admin_super: "any",
    admin_billing: "any",
    owner: "own",
    sales: "own",
    tech: "own",
  },
  "billing.accounts": {
    admin_super: "any",
    admin_billing: "any",
    owner: "own",
    sales: "own",
  },
  "dealers.manage": {
    admin_super: "any",
    owner: "own",
  },
  "users.manage": {
    admin_super: "any",
    owner: "own",
  },
  "grants.revoke": {
    admin_super: "any",
    owner: "own",
    sales: "own",
    tech: "own",
  },
  "requests.create": {
    admin_super: "any",
    admin_billing: "any",
    admin_sales: "any",
    admin_support: "any",
    owner: "own",
    sales: "own",
    tech: "own",
  },
  "requests.comment": {
    admin_super: "any",
    admin_billing: "any",
    admin_sales: "any",
    admin_support: "any",
    owner: "own",
    sales: "own",
    tech: "own",
  },
  "requests.run": {
    admin_super: "any",
    admin_support: "any",
  },
  "requests.manage": {
    admin_super: "any",
    admin_support: "any",
  },
};

export function writeScope(role: Role, permission: Permission): WriteScope | null {
  return WRITE_SCOPES[permission]?.[role] ?? null;
}

export const ADMIN_EMAIL_DOMAIN = "phoneware.us";

export function assertRoleEmail(role: Role, email: string): void {
  if (!isAdminRole(role)) {
    return;
  }
  const normalized = email.trim().toLowerCase();
  const atIndex = normalized.indexOf("@");
  if (atIndex <= 0 || atIndex !== normalized.lastIndexOf("@")) {
    throw new Error("Phoneware admins need a phoneware.us email");
  }
  const domain = normalized.slice(atIndex + 1);
  if (domain !== ADMIN_EMAIL_DOMAIN) {
    throw new Error("Phoneware admins need a phoneware.us email");
  }
}

export const AUDIT_ACTIONS = [
  "act_as.start",
  "act_as.end",
  "quotes.create",
  "quotes.update",
  "quotes.send",
  "quotes.clone",
  "quotes.mark_sold",
  "quotes.mark_lost",
  "quotes.approval_decision",
  "quotes.delete",
  "orders.update",
  "orders.retry",
  "orders.approve_billing",
  "customers.create",
  "customers.add_contact",
  "customers.assign",
  "customers.update_billing",
  "customers.update_category",
  "customers.update_account_number",
  "dealers.create",
  "dealers.update",
  "dealers.associate_reseller",
  "users.invite",
  "users.update",
  "quote_templates.create",
  "quote_templates.update",
  "quote_templates.delete",
  "quote_intakes.create",
  "quote_intakes.confirm",
  "quote_intakes.discard",
  "catalog.create",
  "catalog.update",
  "catalog.set_billing_link",
  "catalog.dismiss_link",
  "catalog.delete_dismissal",
  "catalog.sync_plan",
  "catalog.sync_apply",
  "sync.run",
  "grants.revoke",
  "dealers.sync_ns",
  "catalog.autotask_action",
  "billing_review.decide",
  "accounts.refresh",
  "accounts.dismiss",
  "accounts.undo",
  "customer_accounts.decide",
  "customer_accounts.undo",
  "feedback.create",
  "feedback.draft_update",
  "feedback.draft_discard",
  "feedback.comment",
  "feedback.start",
  "feedback.retry_filing",
  "feedback.retry_run",
  "feedback.close",
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export function isAuditAction(action: string): action is AuditAction {
  return (AUDIT_ACTIONS as readonly string[]).includes(action);
}

export const AUDIT_ACTION_TEXT: Record<AuditAction, string> = {
  "act_as.start": "Started acting as",
  "act_as.end": "Stopped acting as",
  "quotes.create": "Created quote",
  "quotes.update": "Updated quote",
  "quotes.send": "Sent quote",
  "quotes.clone": "Cloned quote",
  "quotes.mark_sold": "Marked quote sold",
  "quotes.mark_lost": "Marked quote lost",
  "quotes.approval_decision": "Decided quote approval",
  "quotes.delete": "Deleted quote",
  "orders.update": "Updated order",
  "orders.retry": "Retried order",
  "orders.approve_billing": "Approved billing on order",
  "customers.create": "Added customer",
  "customers.add_contact": "Added contact to customer",
  "customers.assign": "Assigned customer",
  "customers.update_billing": "Updated customer billing",
  "customers.update_category": "Updated customer category",
  "customers.update_account_number": "Updated customer account number",
  "dealers.create": "Created dealer",
  "dealers.update": "Updated dealer",
  "dealers.associate_reseller": "Associated reseller for dealer",
  "dealers.sync_ns": "Synced NetSapiens for dealer",
  "users.invite": "Invited",
  "users.update": "Updated user",
  "quote_templates.create": "Created quote template",
  "quote_templates.update": "Updated quote template",
  "quote_templates.delete": "Deleted quote template",
  "quote_intakes.create": "Created quote intake",
  "quote_intakes.confirm": "Confirmed quote intake",
  "quote_intakes.discard": "Discarded quote intake",
  "catalog.create": "Created catalog item",
  "catalog.update": "Updated catalog item",
  "catalog.set_billing_link": "Linked catalog item",
  "catalog.dismiss_link": "Dismissed catalog link",
  "catalog.delete_dismissal": "Reopened catalog link",
  "catalog.sync_plan": "Planned catalog sync",
  "catalog.sync_apply": "Applied catalog sync",
  "catalog.autotask_action": "Performed Autotask action on catalog",
  "billing_review.decide": "Decided billing review item",
  "accounts.refresh": "Refreshed accounts",
  "accounts.dismiss": "Dismissed account match",
  "accounts.undo": "Undid account match dismissal",
  "customer_accounts.decide": "Decided customer account link",
  "customer_accounts.undo": "Undid customer account decision",
  "sync.run": "Ran sync",
  "grants.revoke": "Revoked AI connection",
  "feedback.create": "Created change request",
  "feedback.draft_update": "Updated a change request draft",
  "feedback.draft_discard": "Discarded a change request draft",
  "feedback.comment": "Commented on change request",
  "feedback.start": "Started change request work",
  "feedback.retry_filing": "Retried change request filing",
  "feedback.retry_run": "Retried change request work",
  "feedback.close": "Closed change request",
};

export function assertUserInvariants(user: {
  role: Role;
  dealerId: string | null;
  scope: string;
  email?: string;
}): void {
  if (isAdminRole(user.role)) {
    if (user.dealerId !== null) {
      throw new Error(`Admin role "${user.role}" must have dealerId: null, got "${user.dealerId}"`);
    }
    if (user.scope !== "all") {
      throw new Error(`Admin role "${user.role}" must have scope: "all", got "${user.scope}"`);
    }
    if (user.email !== undefined) {
      assertRoleEmail(user.role, user.email);
    }
  } else if (isDealerRole(user.role)) {
    if (user.dealerId === null || typeof user.dealerId !== "string" || user.dealerId.trim().length === 0) {
      throw new Error(`Dealer role "${user.role}" requires a non-null string dealerId`);
    }
  } else {
    throw new Error(`Unknown role "${user.role}"`);
  }
}
